import React from 'react';
import { Hourglass, MapPin } from 'lucide-react';
import { formatPhone, locationOf, retentionCountdown } from '../../../lib/phoneLifecycle.js';

/**
 * "Numbers Pending Deletion": lines held for 30 days, restorable until the timer lapses.
 * Renders nothing when there are none.
 * @param {{ numbers: Object[], busy?: boolean, onRestore: (n: Object) => void, onExtend?: (n: Object) => void, onDeletePermanently: (n: Object) => void }} props
 * @returns {JSX.Element|null}
 */
export default function PendingDeletionSection({ numbers, busy = false, onRestore, onExtend, onDeletePermanently }) {
  if (!numbers?.length) return null;
  return (
    <div className="panel p-5 space-y-4" data-testid="pending-deletion">
      <div className="flex items-center gap-2 font-display font-bold">
        <Hourglass className="w-4 h-4 text-amber-300" /> Numbers Pending Deletion
      </div>
      <p className="text-xs text-white/55">These numbers are held for 30 days and then released automatically. Restore one any time before that.</p>
      <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {numbers.map((n) => {
          const c = retentionCountdown(n.pendingDeletionExpiresAt);
          return (
            <li key={n._id} className="rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-4 space-y-3">
              <div>
                <div className="font-mono text-lg font-semibold">{formatPhone(n.number)}</div>
                <div className="flex items-center gap-1 text-xs text-white/60 mt-0.5">
                  <MapPin className="w-3 h-3" /> {locationOf(n)}{n.areaCode ? <span className="text-white/30">· {n.areaCode}</span> : null}
                </div>
                <div className="text-xs text-amber-300 mt-1">{c.text}</div>
              </div>
              <div className="flex gap-2 pt-3 border-t border-white/10">
                <button type="button" disabled={busy} onClick={() => onRestore(n)} className="px-3 py-1.5 rounded-md bg-civix-purple text-white text-xs font-bold hover:bg-civix-purpleDeep disabled:opacity-50">Restore Number</button>
                {onExtend && (
                  <button type="button" disabled={busy} onClick={() => onExtend(n)} className="px-3 py-1.5 rounded-md border border-civix-purple/50 text-civix-purpleSoft text-xs font-bold hover:bg-civix-purple/10 disabled:opacity-50">Extend 30 days ($1.00)</button>
                )}
                <button type="button" disabled={busy} onClick={() => onDeletePermanently(n)} className="px-3 py-1.5 rounded-md border border-rose-400/50 text-rose-300 text-xs font-bold hover:bg-rose-500/10 disabled:opacity-50">Delete Permanently</button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
